// src/services/razorpay.service.js
import { api } from './api';

/**
 * Razorpay Payment Service
 * Handles order creation and payment processing for tier-based plans
 */

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_XXXXXXXXXXXXX';

// ─── Tier Configuration (must match backend TIER_CONFIG) ────────────────────
export const TIER_CONFIG = {
    starter: {
        label: 'Starter',
        baseFee: 2499,
        baseEmployees: 25,
        perEmployee: 99,
        maxEmployees: 50,
        aiCredits: 50,
        color: 'blue',
        popular: false,
    },
    pro: {
        label: 'Pro',
        baseFee: 4999,
        baseEmployees: 25,
        perEmployee: 79,
        maxEmployees: 150,
        aiCredits: 50,
        color: 'purple',
        popular: true,
    },
    enterprise: {
        label: 'Enterprise',
        baseFee: 9999,
        baseEmployees: 25,
        perEmployee: 59,
        maxEmployees: 500,
        aiCredits: null, // Unlimited
        color: 'amber',
        popular: false,
    }
};

/**
 * Calculate plan amount for a tier + employee count
 * @param {string} tier - starter, pro, enterprise
 * @param {number} employeeCount - Total employees
 * @returns {Object} Amount details
 */
export const calculatePlanAmount = (tier, employeeCount = 25) => {
    const config = TIER_CONFIG[tier];
    if (!config) throw new Error(`Invalid tier: ${tier}`);

    const count = Math.max(1, Math.min(employeeCount, config.maxEmployees));
    const extraEmployees = Math.max(0, count - config.baseEmployees);
    const baseAmount = config.baseFee + (extraEmployees * config.perEmployee);
    const gst = Math.round(baseAmount * 0.18);
    const totalAmount = baseAmount + gst;

    return {
        baseAmount,
        gst,
        totalAmount,
        amountInPaise: totalAmount * 100,
        employeeCount: count,
        extraEmployees,
        perEmployeeRate: config.perEmployee,
        baseFee: config.baseFee,
        tier,
        label: config.label,
    };
};

/**
 * Create a Razorpay order for the selected tier
 * @param {Object} planData - Plan details
 * @param {string} planData.planType - starter, pro, enterprise
 * @param {number} planData.employeeCount - Number of employees
 * @returns {Promise<Object>} Order details
 */
export const createRazorpayOrder = async (planData) => {
    try {
        // Calculate amount server-side for security, but send expected amount for validation
        const pricing = calculatePlanAmount(planData.planType, planData.employeeCount);

        const response = await api.post('/payments/razorpay/create-order', {
            planType: planData.planType,
            employeeCount: planData.employeeCount,
            amount: pricing.amountInPaise,
            currency: 'INR',
            isUpgrade: planData.isUpgrade || false,
            notes: {
                tier: planData.planType,
                employeeCount: planData.employeeCount
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating Razorpay order:', error);
        throw error;
    }
};

/**
 * Verify payment signature and update subscription
 * @param {Object} paymentData - Payment details
 * @returns {Promise<Object>} Verification result
 */
export const verifyRazorpayPayment = async (paymentData) => {
    try {
        const response = await api.post('/payments/razorpay/verify', {
            razorpay_payment_id: paymentData.razorpay_payment_id,
            razorpay_order_id: paymentData.razorpay_order_id,
            razorpay_signature: paymentData.razorpay_signature,
            planType: paymentData.planType,
            employeeCount: paymentData.employeeCount
        });
        return response.data;
    } catch (error) {
        console.error('Error verifying Razorpay payment:', error);
        throw error;
    }
};

/**
 * Get active subscription details including tier and AI credits
 * @returns {Promise<Object>} Subscription details
 */
export const getSubscriptionDetails = async () => {
    try {
        const response = await api.get('/payments/subscription');
        return response.data;
    } catch (error) {
        console.error('Error fetching subscription details:', error);
        throw error;
    }
};

/**
 * Cancel subscription
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelSubscription = async () => {
    try {
        const response = await api.post('/payments/subscription/cancel');
        return response.data;
    } catch (error) {
        console.error('Error cancelling subscription:', error);
        throw error;
    }
};

/**
 * Get payment history
 * @returns {Promise<Array>} Payment history
 */
export const getPaymentHistory = async () => {
    try {
        const response = await api.get('/payments/history');
        return response.data;
    } catch (error) {
        console.error('Error fetching payment history:', error);
        throw error;
    }
};

/**
 * Initialize Razorpay checkout
 * @param {Object} options - Razorpay checkout options
 * @param {Function} onSuccess - Success callback
 * @param {Function} onError - Error callback
 * @returns {Promise<Object>} Payment result
 */
export const initiateRazorpayPayment = (options, onSuccess, onError) => {
    return new Promise((resolve, reject) => {
        // If SDK already loaded, use it directly
        if (window.Razorpay) {
            const razorpay = new window.Razorpay({
                key: RAZORPAY_KEY_ID,
                ...options
            });
            razorpay.on('payment.failed', (response) => {
                onError(response);
                reject(response);
            });
            razorpay.open();
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
            const razorpay = new window.Razorpay({
                key: RAZORPAY_KEY_ID,
                ...options
            });
            razorpay.on('payment.failed', (response) => {
                onError(response);
                reject(response);
            });
            razorpay.open();
            resolve();
        };
        script.onerror = () => {
            reject(new Error('Failed to load Razorpay SDK'));
        };
        document.body.appendChild(script);
    });
};

/**
 * Check if company can add more employees under current plan
 * @returns {Promise<Object>} { canAdd, currentCount, limit, tier, maxEmployees }
 */
export const canAddEmployee = async () => {
    try {
        const response = await api.get('/payments/subscription/can-add-employee');
        return response.data;
    } catch (error) {
        console.error('Error checking employee limit:', error);
        return { canAdd: true, currentCount: 0, limit: 999, tier: 'unknown', maxEmployees: 999 };
    }
};

/**
 * Get prorated upgrade price
 * @param {string} tier - Target tier
 * @param {number} employeeCount - Employee count
 * @returns {Promise<Object>} { fullPrice, credit, prorated, daysRemaining, totalDays }
 */
export const getUpgradePrice = async (tier, employeeCount = 25) => {
    try {
        const response = await api.get('/payments/subscription/upgrade-price', {
            params: { tier, employeeCount }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching upgrade price:', error);
        throw error;
    }
};

/**
 * Schedule a downgrade for end of billing cycle
 * @param {string} tier - Target lower tier
 * @param {number} employeeCount - Employee count for new tier
 * @returns {Promise<Object>} { scheduled, effectiveDate, newTier }
 */
export const scheduleDowngrade = async (tier, employeeCount = 25) => {
    try {
        const response = await api.post('/payments/subscription/schedule-downgrade', {
            tier, employeeCount
        });
        return response.data;
    } catch (error) {
        console.error('Error scheduling downgrade:', error);
        throw error;
    }
};

/**
 * Cancel a scheduled downgrade
 * @returns {Promise<Object>} { success, message }
 */
export const cancelScheduledChange = async () => {
    try {
        const response = await api.post('/payments/subscription/cancel-scheduled');
        return response.data;
    } catch (error) {
        console.error('Error cancelling scheduled change:', error);
        throw error;
    }
};

export default {
    createRazorpayOrder,
    verifyRazorpayPayment,
    getSubscriptionDetails,
    cancelSubscription,
    getPaymentHistory,
    initiateRazorpayPayment,
    calculatePlanAmount,
    canAddEmployee,
    getUpgradePrice,
    scheduleDowngrade,
    cancelScheduledChange,
    TIER_CONFIG
};
